import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'

// Cloudflare Turnstile explicit-render script. Loaded once, lazily, the
// first time a widget actually needs to mount — auth pages that never
// render the widget (Turnstile disabled) never pull the script.
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
let scriptPromise = null

function loadTurnstileScript() {
  if (typeof window !== 'undefined' && window.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = SCRIPT_SRC
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => {
      // Allow a later mount to retry rather than caching the failure.
      scriptPromise = null
      reject(new Error('turnstile_script_failed'))
    }
    document.head.appendChild(s)
  })
  return scriptPromise
}

/**
 * Probe the public Turnstile config once. Mirrors the Google SSO block's
 * probe of `/auth/google/config`: returns `{ enabled, siteKey, loaded }`
 * so a form can decide whether to render the widget and require a token.
 * `enabled` is true only when the backend has the master switch on AND a
 * site key configured.
 */
export function useTurnstileConfig() {
  const [config, setConfig] = useState({
    enabled: false,
    siteKey: null,
    loaded: false,
  })
  useEffect(() => {
    let cancelled = false
    api
      .get('/auth/turnstile/config')
      .then((res) => {
        if (cancelled) return
        setConfig({
          enabled: Boolean(res.data?.enabled && res.data?.siteKey),
          siteKey: res.data?.siteKey ?? null,
          loaded: true,
        })
      })
      .catch(() => {
        if (!cancelled) setConfig({ enabled: false, siteKey: null, loaded: true })
      })
    return () => {
      cancelled = true
    }
  }, [])
  return config
}

/**
 * Renders the Cloudflare Turnstile challenge widget and reports its
 * one-time token via `onToken`. Pass a stable setter (e.g. a `useState`
 * dispatcher) so the mount effect doesn't re-run every render. The token
 * is cleared (empty string) when the challenge expires or errors so the
 * parent form can disable submit until the user re-verifies.
 */
export function TurnstileWidget({ siteKey, onToken }) {
  const containerRef = useRef(null)
  const widgetIdRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onToken(token),
          'expired-callback': () => onToken(''),
          'error-callback': () => onToken(''),
        })
      })
      .catch(() => {
        // Script blocked (offline / ad-blocker). Leave the token empty;
        // the parent keeps submit disabled and shows its own message.
      })
    return () => {
      cancelled = true
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current)
        } catch {
          /* widget already torn down */
        }
        widgetIdRef.current = null
      }
    }
  }, [siteKey, onToken])

  return <div ref={containerRef} className="flex justify-center" />
}
